import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { setFreeeCostTag, type DashboardData, type QueryResult } from './api'
import { DataSourcesView } from './components/DataSourcesView'
import { StaleLine } from './components/FreshnessStatus'
import { SourceMap } from './components/SourceMap'
import { KpiRow } from './components/KpiRow'
import { CashFlowChart } from './components/CashFlowChart'
import { CashPositionChart } from './components/CashPositionChart'
import { ForecastVsActualChart } from './components/ForecastVsActualChart'
import { BreakevenChart } from './components/BreakevenChart'
import { ReconciliationChart } from './components/ReconciliationChart'
import { Masthead } from './components/Masthead'
import { BalanceReconciliation } from './components/BalanceReconciliation'
import { OverlapReport } from './components/OverlapReport'
import { PlanVsActualPayroll } from './components/PlanVsActualPayroll'
import { PayrollSourceNote, PayrollSourceSwitch } from './components/PayrollSourceSwitch'
import { HeadcountPlanEditor } from './components/HeadcountPlanEditor'
import { ScenarioPlanner } from './components/ScenarioPlanner'
import { FreeeCostsView } from './components/FreeeCostsView'
import { QueryConsole } from './components/QueryConsole'
import { freeeAsOf } from './freeeCosts'
import {
  CASH_MODE_SWITCH_SHOWN,
  MONTH_END_PREVIEW_AVAILABLE,
  currentCashMode,
  defaultCashStorage,
  readCashMode,
  writeCashMode,
  type CashMode,
} from './cashMode'
import { liveBalanceFor } from './charts/cashPoints'
import {
  PAYROLL_SOURCE_LOCKED,
  PAYROLL_SOURCE_SWITCH_SHOWN,
  currentPayrollSource,
  readPayrollSource,
  seriesFor,
  writePayrollSource,
  type PayrollSource,
} from './payrollSource'
import { chartSources, freshnessSummary, sourceNote } from './freshness'
import { firstUnreportedMonth } from './charts/plan'
import { inWindow, type DataWindow } from './timeWindow'
import type { SectionTarget } from './sourceMap/graph'

// The presentation of a loaded dashboard, given the data alone. App owns the
// auth/data/refresh shell around it (sign-in, topbar and its freshness pill);
// everything *below* those — the charts, tables, editor and scenario planner —
// lives here so it can be rendered two ways: by App against the live
// API, and by the preview harness against a fixture (src/preview), which is how
// a change gets a visual review without a running backend (see the dashboard
// CLAUDE.md "Previewing a change"). Keep this a pure function of `data` plus the
// editor's write handles — no fetching, no auth — so both callers stay honest.
//
// Tabs: the GOSPEL "Overview" leadership already reads, "Costs (freee)" — the
// freee AP/opex costs — "Data map", and "Data sources", where the freshness
// pill's detail lives. `initialTab` lets the static preview render any tab (the tab switch itself needs JS, which the offline preview HTML lacks);
// `initialShowPipeline` does the same job for the pipeline switch, and
// `initialExpandedVendors` for the recurring-costs drill-down.
//
// The "where these numbers come from" map (SourceMap) has the Data map tab to
// itself, kept off the Overview so it isn't the first thing a reader meets; the
// Overview links to it in one line. Its section boxes jump here: each chart it
// names is wrapped in a MapTarget whose id comes from sourceMap/graph.ts, and
// the jump scrolls once the target's tab is shown.
//
// A last tab, "SQL", appears only when `runQuery` is given: App passes it for
// a viewer who also holds finance-query (dashboardAccess.canQuery).
//
// The time window (App's picker) is applied twice. The API already trims the
// dated series to it (`from`/`to`); the bank reconciliation and the overlap
// report have no such arguments, so they are filtered here against the window.
// The three forecast charts also pad their axis to the window end and mark where
// the data stops. The live balance is read from the UNFILTERED reconciliation:
// it is the month in progress, relevant whichever window is chosen. freee's
// recurring costs and the transaction drill-down keep their full history on
// purpose (see api.ts).

export type DashboardTab = 'overview' | 'costs' | 'map' | 'sources' | 'sql'

// A scroll target for the source map. A plain div the map can focus and flash;
// the chart inside is untouched.
function MapTarget({ anchor, children }: { anchor: string; children: ReactNode }): JSX.Element {
  return (
    <div id={anchor} className="map-target" tabIndex={-1}>
      {children}
    </div>
  )
}

export function DashboardView({
  data,
  // Renamed: a bare `window` would hide the browser global used below.
  window: dataWindow,
  apiUrl,
  token,
  onPublished,
  onSessionExpired,
  initialTab = 'overview',
  tab: tabProp,
  onTabChange,
  initialShowPipeline = false,
  initialExpandedVendors = [],
  runQuery,
  initialQueryResult = null,
}: {
  data: DashboardData
  // The window `data` was fetched for; see the header comment.
  window: DataWindow
  apiUrl: string
  token: string
  onPublished: () => void
  // Called when a write here (publishing a plan, editing a cost label) finds the
  // session expired, so App can sign the user out to the login screen rather than
  // showing a "sign in again" message on a screen with no way to do so.
  onSessionExpired: (message: string) => void
  initialTab?: DashboardTab
  // App lifts the tab so the top-bar pill can open Data sources; without them
  // (the preview) the view keeps its own tab state.
  tab?: DashboardTab
  onTabChange?: (tab: DashboardTab) => void
  initialShowPipeline?: boolean
  initialExpandedVendors?: string[]
  // Present only for finance-query holders; its absence hides the SQL tab.
  runQuery?: (statement: string) => Promise<QueryResult>
  initialQueryResult?: QueryResult | null
}): JSX.Element {
  const [localTab, setLocalTab] = useState<DashboardTab>(initialTab)
  const tab = tabProp ?? localTab
  const setTab = onTabChange ?? setLocalTab
  const { behindCount } = freshnessSummary(data.sourceFreshness)

  // A jump from the source map: switch tab now, scroll after the panel is
  // shown. Scrolling to an element inside a `hidden` panel does nothing, so the
  // scroll waits for the render in which `tab` has caught up.
  const [pendingJump, setPendingJump] = useState<SectionTarget | null>(null)
  const navigate = (target: SectionTarget): void => {
    setTab(target.tab)
    setPendingJump(target)
  }
  useEffect(() => {
    if (pendingJump === null || pendingJump.tab !== tab) return
    const el = document.getElementById(pendingJump.anchor ?? `panel-${pendingJump.tab}`)
    setPendingJump(null)
    if (el === null) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' })
    if (pendingJump.anchor === null) return
    el.focus({ preventScroll: true })
    // Reduced motion gets the instant scroll and the focus, no flash.
    if (reduced) return
    el.classList.remove('map-target-flash')
    void el.offsetWidth // restart the animation if it was already running
    el.classList.add('map-target-flash')
    el.addEventListener('animationend', () => el.classList.remove('map-target-flash'), {
      once: true,
    })
  }, [pendingJump, tab])
  // ONE pipeline basis for the whole page, even though the switch is drawn on
  // each forecast chart. Per-chart state would let two panels on one screen sit
  // on different bases — a reader comparing the cash line against the in/out
  // bars would be comparing pipeline-inclusive against booked and never know.
  // Off by default: the first read is the current-status view (booked money
  // against expected costs), and the optimistic case is something you ask for.
  const [showPipeline, setShowPipeline] = useState(initialShowPipeline)

  // Which payroll figure the cash charts use (src/payrollSource.ts): GOSPEL
  // row 4 for everyone while PAYROLL_SOURCE_LOCKED is set (the switch is
  // retired); set it to null to bring back the per-viewer switch.
  const [storedPayrollSource, setStoredPayrollSource] = useState<PayrollSource>(() =>
    readPayrollSource(defaultCashStorage()),
  )
  const payrollSource: PayrollSource = currentPayrollSource(storedPayrollSource)
  const changePayrollSource = (next: PayrollSource): void => {
    setStoredPayrollSource(next)
    writePayrollSource(defaultCashStorage(), next)
  }
  const series = seriesFor(payrollSource, data)
  const sources = chartSources(payrollSource)

  // Where the reporting stops and the plan takes over. Derived once here, from
  // the gospel series, because the cash-position chart needs the same boundary
  // and the two must never disagree about it.
  const projectedFrom = firstUnreportedMonth(series.gospel)

  // freee's live balance for the month in progress: the cash_balances row the
  // freee feed keeps overwriting, which the cash line no longer anchors on
  // (it anchors on the latest ENDED month). Any freee row after the anchor is
  // that one.
  const anchorMonth = series.cash[0]?.anchorMonth ?? null
  const liveRow =
    anchorMonth === null
      ? undefined
      : [...series.balance]
          .reverse()
          .find((row) => row.source === 'freee' && row.month > anchorMonth)
  const singleAnchorLive = liveRow ? { month: liveRow.month, balance: liveRow.auditedBalance } : null

  // The month-end-actuals reading (GTM-54) drives the cash chart, the
  // scenario planner and the live marker together. Forced for everyone while
  // CASH_MODE_LOCKED is set (the switch is retired); set it to null to bring
  // back the per-viewer switch.
  const [storedCashMode, setStoredCashMode] = useState<CashMode>(() =>
    readCashMode(defaultCashStorage()),
  )
  const cashMode: CashMode = currentCashMode(storedCashMode)
  const changeCashMode = (next: CashMode): void => {
    setStoredCashMode(next)
    writeCashMode(defaultCashStorage(), next)
  }
  const liveBalance =
    cashMode === 'month-end-actuals'
      ? liveBalanceFor(series.cash, series.balance)
      : singleAnchorLive

  const balanceRows = series.balance.filter((row) => inWindow(row.month, dataWindow))
  const overlapRows = data.pipelineOverlapReport.filter((row) => inWindow(row.month, dataWindow))

  return (
    <>
      <nav className="tabs" role="tablist" aria-label="Dashboard sections">
        <button
          type="button"
          role="tab"
          id="tab-overview"
          aria-controls="panel-overview"
          aria-selected={tab === 'overview'}
          className={tab === 'overview' ? 'active' : ''}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          id="tab-costs"
          aria-controls="panel-costs"
          aria-selected={tab === 'costs'}
          className={tab === 'costs' ? 'active' : ''}
          onClick={() => setTab('costs')}
        >
          Costs (freee)
        </button>
        <button
          type="button"
          role="tab"
          id="tab-map"
          aria-controls="panel-map"
          aria-selected={tab === 'map'}
          className={tab === 'map' ? 'active' : ''}
          onClick={() => setTab('map')}
        >
          Data map
        </button>
        <button
          type="button"
          role="tab"
          id="tab-sources"
          aria-controls="panel-sources"
          aria-selected={tab === 'sources'}
          className={tab === 'sources' ? 'active' : ''}
          onClick={() => setTab('sources')}
        >
          Data sources{behindCount > 0 ? ` (${behindCount})` : ''}
        </button>
        {runQuery !== undefined && (
          <button
            type="button"
            role="tab"
            id="tab-sql"
            aria-controls="panel-sql"
            aria-selected={tab === 'sql'}
            className={tab === 'sql' ? 'active' : ''}
            onClick={() => setTab('sql')}
          >
            SQL
          </button>
        )}
      </nav>

      {/* Every panel stays MOUNTED; the inactive one is hidden. Unmounting on
          tab switch would discard an in-progress plan edit or cost-label edit —
          the same "don't silently lose work" care as the load-path clearing. */}
      <div hidden={tab !== 'overview'} role="tabpanel" id="panel-overview" aria-labelledby="tab-overview">
        <>
          <StaleLine freshness={data.sourceFreshness} onOpen={() => setTab('sources')} />
          <p className="map-pointer">
            Where do these numbers come from?{' '}
            <button type="button" className="link-button" onClick={() => setTab('map')}>
              See the data map
            </button>
          </p>
          {data.headcountPlan === null && (
            <p className="warn-banner" role="alert">
              No headcount &amp; AP plan has been published, so the forecast below — net-new AP, cash
              position and runway — excludes all planned headcount and opex. Publish a plan in the
              editor near the bottom of the page.
            </p>
          )}
          {/* The switch while it is offered; once retired (lock set), a one-line
              note in its place; with the lock off and the kill switch thrown,
              nothing, as before the lock existed. */}
          {PAYROLL_SOURCE_SWITCH_SHOWN ? (
            <PayrollSourceSwitch source={payrollSource} onChange={changePayrollSource} />
          ) : PAYROLL_SOURCE_LOCKED !== null ? (
            <PayrollSourceNote source={payrollSource} />
          ) : null}
          <KpiRow months={series.gospel} showPipeline={showPipeline} />
          {/* With the pipeline off the charts and the KPI row stop agreeing with
              the cash flow table further down, which stays on the database's own
              figures, pipeline included in its own "Net new AR" row. That is
              right — the table is the authoritative record the chart is checked
              against, and it must not start moving with a view control. But two
              different nets for one month on one screen is precisely the "which
              number am I looking at" confusion this project exists to end, so
              say so rather than leaving the reader to notice. */}
          {!showPipeline && (
            <p className="basis-note" role="note">
              Pipeline off: the figures above and the charts below leave out deals not yet signed.
              The cash flow table further down keeps the database&apos;s full figures, with the
              weighted pipeline in its own Net new AR row, so its net can read higher for the same
              month.
            </p>
          )}
          {/* Charts first, then the tables they are drawn from. The chart is the
              faster read; the table is the one you check it against, and it stays
              on the page precisely so the chart never has to be taken on trust. */}
          <MapTarget anchor="section-cash-in-out">
            <CashFlowChart
              months={series.gospel}
              showPipeline={showPipeline}
              onShowPipelineChange={setShowPipeline}
              sourceNote={sourceNote(data.sourceFreshness, sources.cashFlow)}
              payrollLabel={payrollSource === 'gospel' ? 'GOSPEL AP out' : 'Payroll out'}
              windowEnd={dataWindow.to}
            />
          </MapTarget>
          <MapTarget anchor="section-cash-position">
            <CashPositionChart
              months={series.cash}
              showPipeline={showPipeline}
              onShowPipelineChange={setShowPipeline}
              projectedFrom={projectedFrom}
              liveBalance={liveBalance}
              sourceNote={sourceNote(data.sourceFreshness, sources.cashPosition)}
              windowEnd={dataWindow.to}
              mode={cashMode}
              onModeChange={changeCashMode}
              previewAvailable={MONTH_END_PREVIEW_AVAILABLE}
              showModeSwitch={CASH_MODE_SWITCH_SHOWN}
            />
          </MapTarget>
          {/* Always the GOSPEL series (what the weekly copies were saved from),
              with its own pipeline switch: see ForecastVsActualChart. */}
          <MapTarget anchor="section-forecast-vs-actual">
            <ForecastVsActualChart
              weeks={data.cashForecastWeeks}
              months={data.cashPositionByMonthGospelAp}
              sourceNote={sourceNote(data.sourceFreshness, sources.forecastVsActual)}
              windowEnd={dataWindow.to}
            />
          </MapTarget>
          <MapTarget anchor="section-breakeven">
            <BreakevenChart
              months={data.softwarePlByMonth}
              sourceNote={sourceNote(data.sourceFreshness, sources.breakeven)}
              windowEnd={dataWindow.to}
            />
          </MapTarget>
          <MapTarget anchor="section-bank-reconciliation">
            <ReconciliationChart
              rows={balanceRows}
              sourceNote={sourceNote(data.sourceFreshness, sources.reconciliation)}
            />
          </MapTarget>
          <Masthead months={series.gospel} />
          <BalanceReconciliation rows={balanceRows} />
          <MapTarget anchor="section-overlap">
            <OverlapReport rows={overlapRows} />
          </MapTarget>
          <MapTarget anchor="section-plan-vs-actual">
            <PlanVsActualPayroll rows={data.headcountPlanReconciliation} />
            {payrollSource === 'gospel' && (
              <p className="basis-note" role="note">
                {PAYROLL_SOURCE_SWITCH_SHOWN
                  ? 'This chart uses freee payroll, not the payroll source above.'
                  : 'This chart uses freee payroll, not the GOSPEL payroll total used above.'}
              </p>
            )}
          </MapTarget>
          <MapTarget anchor="section-headcount-plan">
            <HeadcountPlanEditor
              plan={data.headcountPlan}
              apiUrl={apiUrl}
              token={token}
              onPublished={onPublished}
              onSessionExpired={onSessionExpired}
            />
          </MapTarget>
          <ScenarioPlanner
            base={series.cash}
            showPipeline={showPipeline}
            mode={cashMode}
          />
          <footer className="footnote">
            Figures are computed in Postgres from booked invoices, payroll actuals and the latest
            forecast snapshot — not from the FP&amp;A workbook. Where a month has no payroll behind it,
            the AP cell is marked rather than filled in.
          </footer>
        </>
      </div>

      <div hidden={tab !== 'costs'} role="tabpanel" id="panel-costs" aria-labelledby="tab-costs">
        <FreeeCostsView
          byLabel={data.freeeCostsByLabelMonth}
          recurring={data.freeeRecurringCosts}
          transactions={data.freeeCostTransactions}
          asOf={freeeAsOf(data.sourceFreshness, new Date().toISOString().slice(0, 10))}
          initialExpandedVendors={initialExpandedVendors}
          saveTag={(input) => setFreeeCostTag(apiUrl, token, input)}
          onReload={onPublished}
          onSessionExpired={onSessionExpired}
        />
      </div>

      <div hidden={tab !== 'map'} role="tabpanel" id="panel-map" aria-labelledby="tab-map">
        <SourceMap freshness={data.sourceFreshness} currentTab={tab} onNavigate={navigate} />
      </div>

      <div hidden={tab !== 'sources'} role="tabpanel" id="panel-sources" aria-labelledby="tab-sources">
        <DataSourcesView freshness={data.sourceFreshness} />
      </div>

      {runQuery !== undefined && (
        <div hidden={tab !== 'sql'} role="tabpanel" id="panel-sql" aria-labelledby="tab-sql">
          <QueryConsole run={runQuery} onSessionExpired={onSessionExpired} initialResult={initialQueryResult} />
        </div>
      )}
    </>
  )
}
